import React, { useState, useMemo } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Pagination,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  FaEdit,
  FaTrash,
  FaCopy,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import moment from 'moment';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { cleanCpfCnpj } from '~/Utils/Helpers';
import * as api from '~/Services/Service';
import FiltroAlerta from './components/FiltroAlerta';
import ModalAlerta from './components/ModalAlerta';
import ModalReplicar from './components/ModalReplicar';

const PAGE_SIZE = 50;

const AlertasClientes = () => {
  const [alertas, setAlertas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [filtros, setFiltros] = useState({
    codigoGrupoEmpresa: null,
    codigoConta: null,
    cpf: '',
    nome: '',
    status: 'V',
    page: 1,
  });
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [alertaSelecionado, setAlertaSelecionado] = useState(null);
  const [modalAlerta, setModalAlerta] = useState({
    aberto: false,
    modo: 'create',
    alerta: null,
  });
  const [modalReplicar, setModalReplicar] = useState({
    aberto: false,
    seqOriginal: null,
  });
  const [confirmacaoExclusao, setConfirmacaoExclusao] = useState({
    show: false,
    item: null,
  });
  const [ordenacao, setOrdenacao] = useState({ campo: 'data', direcao: 'asc' });

  const buscarAlertas = async (filtrosParam) => {
    const f = filtrosParam || filtros;
    setCarregando(true);
    try {
      const documento = f.cpf ? cleanCpfCnpj(f.cpf) : undefined;
      const params = {
        codigoGrupoEmpresa: f.codigoGrupoEmpresa,
        status: f.status || 'V',
        page: f.page || 1,
        pageSize: PAGE_SIZE,
      };
      if (f.codigoConta) params.codigoConta = f.codigoConta;
      if (documento && documento.length >= 11) params.cpf = documento;
      if (f.nome && f.nome.trim()) params.nome = f.nome.trim();

      const res = await api.listarAlertasCliente(params);
      if (res && res.itens) {
        setAlertas(res.itens || []);
        setTotalRegistros(res.total || 0);
      } else {
        const lista = Array.isArray(res) ? res : [];
        setAlertas(lista);
        setTotalRegistros(lista.length);
      }
      setAlertaSelecionado(null);
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao buscar alertas.';
      toast.error(mensagem);
    } finally {
      setCarregando(false);
    }
  };

  const handleBuscar = () => {
    const novosFiltros = { ...filtros, page: 1 };
    setFiltros(novosFiltros);
    buscarAlertas(novosFiltros);
  };

  const handleFiltroChange = (novosFiltros) => {
    setFiltros({ ...novosFiltros, page: 1 });
  };

  const handlePaginaChange = (novaPagina) => {
    setFiltros({ ...filtros, page: novaPagina });
  };

  const handleNovo = () => {
    setModalAlerta({ aberto: true, modo: 'create', alerta: null });
  };

  const handleEditar = (item) => {
    setModalAlerta({ aberto: true, modo: 'edit', alerta: item });
  };

  const handleExcluir = (item) => {
    setConfirmacaoExclusao({ show: true, item: item });
  };

  const handleConfirmarExclusao = async (confirmado) => {
    if (!confirmado) {
      setConfirmacaoExclusao({ show: false, item: null });
      return;
    }
    const item = confirmacaoExclusao.item;
    setConfirmacaoExclusao({ show: false, item: null });
    try {
      await api.removerAlertaCliente({ seq: item.seq });
      toast.success('Alerta excluído com sucesso.');
      buscarAlertas(filtros);
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao excluir alerta.';
      toast.error(mensagem);
    }
  };

  const handleReplicar = (item) => {
    setAlertaSelecionado(item);
    setModalReplicar({ aberto: true, seqOriginal: item.seq });
  };

  const handleSalvarAlerta = () => {
    setModalAlerta({ aberto: false, modo: 'create', alerta: null });
    buscarAlertas();
  };

  const handleFecharAlerta = () => {
    setModalAlerta({ aberto: false, modo: 'create', alerta: null });
  };

  const handleReplicarConcluido = () => {
    setModalReplicar({ aberto: false, seqOriginal: null });
    buscarAlertas();
  };

  const handleFecharReplicar = () => {
    setModalReplicar({ aberto: false, seqOriginal: null });
  };

  const handleOrdenacao = (campo) => {
    setOrdenacao((prev) => {
      if (prev.campo === campo) {
        return { campo: campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' };
      }
      return { campo: campo, direcao: 'asc' };
    });
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo)
      return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return ordenacao.direcao === 'asc' ? (
      <FaSortUp style={{ marginLeft: 4 }} />
    ) : (
      <FaSortDown style={{ marginLeft: 4 }} />
    );
  };

  const thSortStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  const alertasOrdenados = useMemo(() => {
    const campo = ordenacao.campo;
    const dir = ordenacao.direcao === 'asc' ? 1 : -1;
    return alertas.slice().sort((a, b) => {
      let valA = a[campo];
      let valB = b[campo];
      if (valA == null) valA = '';
      if (valB == null) valB = '';
      if (typeof valA === 'string') {
        return dir * valA.toLowerCase().localeCompare(String(valB).toLowerCase());
      }
      return dir * (valA < valB ? -1 : valA > valB ? 1 : 0);
    });
  }, [alertas, ordenacao]);

  const totalPaginas = Math.ceil(totalRegistros / PAGE_SIZE) || 1;
  const paginaAtual = Math.min(filtros.page || 1, totalPaginas);

  const alertasPaginaAtual = useMemo(() => {
    const inicio = (paginaAtual - 1) * PAGE_SIZE;
    return alertasOrdenados.slice(inicio, inicio + PAGE_SIZE);
  }, [alertasOrdenados, paginaAtual]);

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const items = [];
    items.push(
      <Pagination.First
        key="first"
        disabled={paginaAtual === 1}
        onClick={() => handlePaginaChange(1)}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={paginaAtual === 1}
        onClick={() => handlePaginaChange(paginaAtual - 1)}
      />
    );
    const delta = 2;
    const inicio = Math.max(1, paginaAtual - delta);
    const fim = Math.min(totalPaginas, paginaAtual + delta);
    if (inicio > 1) {
      items.push(<Pagination.Ellipsis key="e1" disabled />);
    }
    for (let i = inicio; i <= fim; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === paginaAtual}
          onClick={() => handlePaginaChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    if (fim < totalPaginas) {
      items.push(<Pagination.Ellipsis key="e2" disabled />);
    }
    items.push(
      <Pagination.Next
        key="next"
        disabled={paginaAtual === totalPaginas}
        onClick={() => handlePaginaChange(paginaAtual + 1)}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        disabled={paginaAtual === totalPaginas}
        onClick={() => handlePaginaChange(totalPaginas)}
      />
    );
    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2">
        <small className="text-muted">
          Mostrando {(paginaAtual - 1) * PAGE_SIZE + 1} até{' '}
          {Math.min(paginaAtual * PAGE_SIZE, totalRegistros)} de{' '}
          {totalRegistros} registros
        </small>
        <Pagination size="sm" className="mb-0">
          {items}
        </Pagination>
      </div>
    );
  };

  const textoTruncado = (texto, max) => {
    if (!texto) return '';
    return texto.length > max ? texto.substring(0, max) + '...' : texto;
  };

  const listaVazia = alertas.length === 0;

  return (
    <Container fluid className="px-3 py-3">
      <style>{'\
        .alertas-desktop { display: block; }\
        .alertas-mobile  { display: none; }\
        @media (max-width: 768px) {\
          .alertas-desktop { display: none !important; }\
          .alertas-mobile  { display: block; }\
        }\
      '}</style>

      <Header title="Alertas de Clientes" />

      <FiltroAlerta
        filtros={filtros}
        onChange={handleFiltroChange}
        onBuscar={handleBuscar}
        onNovo={handleNovo}
        carregando={carregando}
      />

      <Card className="shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Alertas
            {totalRegistros > 0 && (
              <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                ({totalRegistros}{' '}
                {totalRegistros === 1 ? 'registro' : 'registros'})
              </span>
            )}
          </strong>
        </Card.Header>
        <Card.Body className="p-0">
          {carregando ? (
            <Loading />
          ) : listaVazia ? (
            <p className="text-muted text-center my-4">
              Nenhum alerta encontrado.
            </p>
          ) : (
            <>
              {/* Desktop */}
              <div className="alertas-desktop">
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th
                        style={Object.assign({ width: '120px' }, thSortStyle)}
                        onClick={() => handleOrdenacao('apelido_grupo_empresa')}
                      >
                        GE{iconeOrdenacao('apelido_grupo_empresa')}
                      </th>
                      <th
                        style={thSortStyle}
                        onClick={() => handleOrdenacao('apelido_conta')}
                      >
                        Cliente{iconeOrdenacao('apelido_conta')}
                      </th>
                      <th style={thSortStyle} onClick={() => handleOrdenacao('texto')}>
                        Texto{iconeOrdenacao('texto')}
                      </th>
                      <th
                        style={Object.assign({ width: '110px' }, thSortStyle)}
                        onClick={() => handleOrdenacao('data')}
                      >
                        Criação{iconeOrdenacao('data')}
                      </th>
                      <th
                        style={Object.assign({ width: '110px' }, thSortStyle)}
                        onClick={() => handleOrdenacao('expira')}
                      >
                        Expiração{iconeOrdenacao('expira')}
                      </th>
                      <th
                        style={Object.assign({ width: '140px' }, thSortStyle)}
                        onClick={() => handleOrdenacao('nome_usuario')}
                      >
                        Usuário{iconeOrdenacao('nome_usuario')}
                      </th>
                      <th style={{ width: '110px', textAlign: 'center' }}>
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {alertasPaginaAtual.map((item) => (
                      <tr
                        key={item.seq}
                        style={{
                          backgroundColor:
                            alertaSelecionado && alertaSelecionado.seq === item.seq
                              ? '#e8f0fe'
                              : undefined,
                          cursor: 'pointer',
                        }}
                        onClick={() => setAlertaSelecionado(item)}
                      >
                        <td>{item.apelido_grupo_empresa}</td>
                        <td>{item.apelido_conta}</td>
                        <td title={item.texto}>
                          {textoTruncado(item.texto, 60)}
                        </td>
                        <td>
                          {item.data
                            ? moment(item.data).format('DD/MM/YYYY')
                            : '—'}
                        </td>
                        <td>
                          {item.expira
                            ? moment(item.expira).format('DD/MM/YYYY')
                            : '—'}
                        </td>
                        <td>{item.nome_usuario || '—'}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          <Button
                            variant="outline-primary"
                            title="Editar"
                            className="mr-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditar(item);
                            }}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-secondary"
                            title="Replicar"
                            className="mr-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReplicar(item);
                            }}
                          >
                            <FaCopy />
                          </Button>
                          <Button
                            variant="outline-danger"
                            title="Excluir"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExcluir(item);
                            }}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile */}
              <div className="alertas-mobile p-2">
                {alertasPaginaAtual.map((item) => (
                  <Card key={item.seq} className="mb-2 shadow-sm">
                    <Card.Body className="py-2 px-3">
                      <div className="mb-1">
                        <strong style={{ fontSize: '0.9rem' }}>
                          {item.apelido_conta}
                        </strong>
                        <small className="text-muted ml-2">
                          {item.apelido_grupo_empresa}
                        </small>
                      </div>
                      <div
                        style={{ fontSize: '0.85rem', marginBottom: '4px' }}
                        className="text-secondary"
                      >
                        {textoTruncado(item.texto, 80)}
                      </div>
                      <div style={{ fontSize: '0.8rem' }} className="mb-2">
                        <span className="mr-2">
                          Criado:{' '}
                          {item.data
                            ? moment(item.data).format('DD/MM/YYYY')
                            : '—'}
                        </span>
                        <span>
                          Expira:{' '}
                          {item.expira
                            ? moment(item.expira).format('DD/MM/YYYY')
                            : '—'}
                        </span>
                      </div>
                      <div className="d-flex justify-content-end">
                        <Button
                          variant="outline-primary"
                          className="mr-1"
                          onClick={() => handleEditar(item)}
                        >
                          <FaEdit className="mr-1" /> Editar
                        </Button>
                        <Button
                          variant="outline-secondary"
                          className="mr-1"
                          onClick={() => handleReplicar(item)}
                        >
                          <FaCopy className="mr-1" /> Replicar
                        </Button>
                        <Button
                          variant="outline-danger"
                          onClick={() => handleExcluir(item)}
                        >
                          <FaTrash className="mr-1" /> Excluir
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </>
          )}
          {renderizarPaginacao()}
        </Card.Body>
      </Card>

      <ModalConfirmacao
        show={confirmacaoExclusao.show}
        title="Confirmar Exclusão"
        message={
          confirmacaoExclusao.item
            ? 'Deseja excluir o alerta do cliente "' +
              (confirmacaoExclusao.item.apelido_conta || '') +
              '"?'
            : ''
        }
        onClose={handleConfirmarExclusao}
      />

      <ModalAlerta
        aberto={modalAlerta.aberto}
        modo={modalAlerta.modo}
        alertaEditar={modalAlerta.alerta}
        codigoGEFiltro={filtros.codigoGrupoEmpresa}
        onFechar={handleFecharAlerta}
        onSalvar={handleSalvarAlerta}
      />

      <ModalReplicar
        aberto={modalReplicar.aberto}
        seqOriginal={modalReplicar.seqOriginal}
        onFechar={handleFecharReplicar}
        onReplicar={handleReplicarConcluido}
      />
    </Container>
  );
};

export default AlertasClientes;
